import React, { useMemo, useState, memo, useCallback } from "react";
import { motion } from "framer-motion";
import Sidebar from "../components/Sidebar";
import OutfitForm from "../components/OutfitForm";
import useWardrobe from "../hooks/useWardrobe";
import { generateOutfitService } from "../services/outfitService";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { ALWAYS_PREMIUM } from "../config/featureFlags";
import { Wand2, Clock, History, Layers, RotateCcw, Shirt, Sparkles } from "lucide-react";
import ToastContainer from "../components/ToastContainer";
import useToast from "../hooks/useToast";
import ClothesSwiper from "../components/ClothesSwiper";
import "../styles/Dashboard.css";

const CATEGORIES = [
    {
        key: "tops",
        types: ["t-shirt", "sweat", "veste", "chemise", "pull", "haut", "gilet"],
    },
    {
        key: "bottoms",
        types: ["pantalon", "jean", "short", "jupe", "jogging"],
    },
    {
        key: "shoes",
        types: ["chaussures", "baskets", "bottes", "sandales", "talons"],
    },
];

// Composant mémorisé pour éviter les re-renders inutiles
const MemoizedClothesSwiper = memo(ClothesSwiper);

const OutfitsToday = () => {
    const { wardrobe } = useWardrobe();
    const [selectedOutfit, setSelectedOutfit] = useState({
        tops: [],
        bottoms: [],
        shoes: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [lastGeneratedAt, setLastGeneratedAt] = useState(null);
    const [formData, setFormData] = useState({
        occasion: "",
        style: "chic",
        season: "ete",
        color: "",
    });
    const [isGenerating, setIsGenerating] = useState(false);
    const { toasts, removeToast, success, error: showError } = useToast();

    const itemsByCategory = useMemo(() => {
        if (!wardrobe.length) {
            return CATEGORIES.reduce((acc, category) => {
                acc[category.key] = [{ id: `empty-${category.key}`, __empty: true }];
                return acc;
            }, {});
        }

        return CATEGORIES.reduce((acc, category) => {
            const items = wardrobe.filter((item) =>
                category.types.includes(item.type?.toLowerCase())
            );
            acc[category.key] = items.length
                ? items
                : [{ id: `empty-${category.key}`, __empty: true }];
            return acc;
        }, {});
    }, [wardrobe]);

    const selectedIds = useMemo(() => {
        const ids = CATEGORIES.reduce((acc, category) => {
            acc[category.key] = selectedOutfit[category.key]?.[0]?.id || null;
            return acc;
        }, {});
        if (process.env.NODE_ENV !== 'production') {
            if (Object.values(ids).some(id => id !== null)) {
                console.log("[OUTFITS-TODAY] ➡️ IDs sélectionnés:", ids);
            }
        }
        return ids;
    }, [selectedOutfit]);

    const favoriteCount = useMemo(
        () => wardrobe.filter((item) => item.favori).length,
        [wardrobe]
    );

    const getRelativeTime = useCallback((date) => {
        if (!date) return "Jamais";
        const diffMs = Date.now() - date.getTime();
        const minutes = Math.max(0, Math.round(diffMs / 60000));
        if (minutes < 1) return "À l'instant";
        if (minutes < 60) return `Il y a ${minutes} min`;
        const hours = Math.round(minutes / 60);
        if (hours < 24) return `Il y a ${hours} h`;
        const days = Math.round(hours / 24);
        if (days < 7) return `Il y a ${days} j`;
        return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
        });
    }, []);

    const lastGeneratedLabel = useMemo(
        () => getRelativeTime(lastGeneratedAt),
        [getRelativeTime, lastGeneratedAt]
    );

    const hasSelection = useMemo(
        () =>
            Object.values(selectedOutfit).some(
                (items) => Array.isArray(items) && items.length > 0
            ),
        [selectedOutfit]
    );

    const resolvedRecentOutfits = useMemo(
        () =>
            recentOutfits.map((outfit) => ({
                ids: outfit,
                top: wardrobe.find((item) => item.id === outfit.top) || null,
                bottom: wardrobe.find((item) => item.id === outfit.bottom) || null,
                shoes: wardrobe.find((item) => item.id === outfit.shoes) || null,
            })),
        [recentOutfits, wardrobe]
    );

    const handleRestoreOutfit = useCallback(
        (outfit) => {
            const restoredOutfit = {
                tops: outfit.top ? [outfit.top] : [],
                bottoms: outfit.bottom ? [outfit.bottom] : [],
                shoes: outfit.shoes ? [outfit.shoes] : [],
            };
            setSelectedOutfit(restoredOutfit);
            success("Tenue rechargée");
        },
        [success]
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const [recentOutfits, setRecentOutfits] = useState([]); // Historique des 5 dernières tenues

    const generateOutfit = async () => {
        if (process.env.NODE_ENV !== 'production') {
            console.log("[OUTFITS-TODAY] Début de la génération d'outfit");
            console.log("[OUTFITS-TODAY] FormData:", formData);
            console.log("[OUTFITS-TODAY] Garde-robe actuelle:", wardrobe.length, "vêtements");
        }
        
        setIsLoading(true);
        setIsGenerating(true);
        try {
            if (!auth.currentUser) {
                throw new Error("Utilisateur non connecté");
            }

            const userDocRef = doc(db, "users", auth.currentUser.uid);
            const userDocSnap = await getDoc(userDocRef);
            const userData = userDocSnap.exists() ? userDocSnap.data() : {};

            const today = new Date().toISOString().split("T")[0];
            const plan = userData?.plan || "free";
            const limits = userData?.limits || {};
            const dailyLimit = ALWAYS_PREMIUM ? null : (limits.dailyGenerations ?? (plan === "premium" ? null : 1));
            const usage = userData?.generationUsage;

            let usageCountToday = 0;
            if (usage?.date === today) {
                usageCountToday = usage.count || 0;
            } else if (userData?.lastGeneration === today) {
                usageCountToday = 1;
            }

            if (dailyLimit !== null && usageCountToday >= dailyLimit) {
                throw new Error(
                    "Vous avez atteint votre limite quotidienne de générations IA. Passez au Premium pour en profiter sans limite."
                );
            }

            if (process.env.NODE_ENV !== 'production') {
                console.log("[OUTFITS-TODAY] Appel du service de génération...");
            }
            const outfitSelection = await generateOutfitService(wardrobe, formData);
            if (process.env.NODE_ENV !== 'production') {
                console.log("[OUTFITS-TODAY] Outfit reçu du service:", outfitSelection);
            }

            // Vérifier si cette tenue n'est pas une répétition récente (2 dernières tenues)
            const outfitSignature = `${outfitSelection.top}-${outfitSelection.bottom}-${outfitSelection.shoes}`;
            if (process.env.NODE_ENV !== 'production') {
                console.log("[OUTFITS-TODAY] Signature de l'outfit:", outfitSignature);
            }
            if (process.env.NODE_ENV !== 'production') {
                console.log("[OUTFITS-TODAY] Historique des outfits récents:", recentOutfits);
            }
            
            // Ne vérifier que les 2 dernières tenues pour éviter trop de restrictions
            const isRecentRepeat = recentOutfits.slice(0, 2).some(recent =>
                `${recent.top}-${recent.bottom}-${recent.shoes}` === outfitSignature
            );
            if (process.env.NODE_ENV !== 'production') {
                console.log("[OUTFITS-TODAY] Est une répétition des 2 dernières?", isRecentRepeat);
            }

            let finalSelection = outfitSelection;
            
            if (isRecentRepeat && recentOutfits.length > 0) {
                // Si c'est une répétition, essayer UNE SEULE fois avec température plus élevée
                if (process.env.NODE_ENV !== 'production') console.log("[OUTFITS-TODAY] Tenue répétée détectée, nouvelle tentative...");
                try {
                    const modifiedFormData = {
                        ...formData,
                        _retry: true // Marqueur pour indiquer que c'est une nouvelle tentative
                    };
                    const fallbackSelection = await generateOutfitService(wardrobe, modifiedFormData);
                    if (process.env.NODE_ENV !== 'production') console.log("[OUTFITS-TODAY] Fallback selection:", fallbackSelection);
                    finalSelection = fallbackSelection;
                } catch (error) {
                    if (process.env.NODE_ENV !== 'production') console.warn("[OUTFITS-TODAY] Fallback échoué, utilisation de la sélection initiale");
                }
            }
            
            // Construire l'outfit final
            const newOutfit = {
                tops: wardrobe.filter((item) => item.id === finalSelection.top),
                bottoms: wardrobe.filter((item) => item.id === finalSelection.bottom),
                shoes: wardrobe.filter((item) => item.id === finalSelection.shoes),
            };
            if (process.env.NODE_ENV !== 'production') {
                console.log("[OUTFITS-TODAY] Outfit construit:", newOutfit);
                console.log("[OUTFITS-TODAY] - Tops trouvés:", newOutfit.tops.length);
                console.log("[OUTFITS-TODAY] - Bottoms trouvés:", newOutfit.bottoms.length);
                console.log("[OUTFITS-TODAY] - Shoes trouvés:", newOutfit.shoes.length);
            }
            
            setSelectedOutfit(newOutfit);

            // Mettre à jour l'historique (garder les 5 dernières)
            setRecentOutfits(prev => [finalSelection, ...prev.slice(0, 4)]);
            setLastGeneratedAt(new Date());

            success("Tenue générée avec succès !");

            const nextUsageCount = usage?.date === today ? (usage.count || 0) + 1 : 1;

            await setDoc(
                userDocRef,
                {
                    lastGeneration: today,
                    generationUsage: {
                        date: today,
                        count: nextUsageCount,
                    },
                },
                { merge: true }
            );
        } catch (error) {
            console.error("[OUTFITS-TODAY] Erreur lors de la génération:", error);
            const errorMessage = error.message || "Erreur lors de la génération de la tenue";
            showError(errorMessage);
        } finally {
            if (process.env.NODE_ENV !== 'production') console.log("[OUTFITS-TODAY] Fin de la génération");
            setIsLoading(false);
            setIsGenerating(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut",
            },
        },
    };

    return (
        <div className="min-h-screen bg-sand-50 text-ink-900">
            <Sidebar />

            <main className="ml-0 md:ml-24 px-4 py-4 md:px-8 pb-20 md:pb-8">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="w-full max-w-6xl mx-auto space-y-6 md:space-y-8"
                >
                    <motion.div variants={itemVariants} className="space-y-1">
                        <h1 className="text-2xl md:text-4xl font-heading font-semibold text-ink-900">Ton outfit du jour</h1>
                        <p className="text-sm text-ink-600 max-w-2xl">
                            Paramètre la génération et visualise instantanément la tenue proposée par l&apos;IA.
                        </p>
                    </motion.div>

                    <motion.div
                        variants={itemVariants}
                        className="grid gap-3 md:grid-cols-3"
                    >
                        <div className="rounded-3xl border border-sand-100 bg-white/80 p-5 shadow-soft">
                            <span className="text-xs uppercase tracking-[0.18em] text-ink-400">Garde-robe</span>
                            <p className="mt-3 text-3xl font-semibold text-ink-900 font-heading">
                                {wardrobe.length}
                            </p>
                            <p className="text-sm text-ink-500 mt-2">
                                Pièces disponibles pour composer ta tenue.
                            </p>
                        </div>
                        <div className="rounded-3xl border border-sand-100 bg-white/80 p-5 shadow-soft">
                            <span className="text-xs uppercase tracking-[0.18em] text-ink-400">Favoris</span>
                            <p className="mt-3 text-3xl font-semibold text-ink-900 font-heading">
                                {favoriteCount}
                            </p>
                            <p className="text-sm text-ink-500 mt-2">
                                Pièces chouchou prêtes à être proposées par l&apos;IA.
                            </p>
                        </div>
                        <div className="rounded-3xl border border-sand-100 bg-white/80 p-5 shadow-soft">
                            <span className="text-xs uppercase tracking-[0.18em] text-ink-400 flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                Dernière génération
                            </span>
                            <p className="mt-3 text-3xl font-semibold text-ink-900 font-heading">
                                {hasSelection ? lastGeneratedLabel : "Aucune"}
                            </p>
                            <p className="text-sm text-ink-500 mt-2">
                                {hasSelection
                                    ? "Tenue actuelle disponible ci-dessous."
                                    : "Lance une génération pour obtenir ta première proposition."}
                            </p>
                        </div>
                    </motion.div>

                    <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-10 lg:items-start">
                        <motion.section
                            className="bg-white/90 rounded-[32px] p-5 sm:p-6 shadow-[0_28px_70px_rgba(17,18,26,0.08)] border border-sand-100 backdrop-blur w-full lg:w-[360px]"
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="p-2.5 rounded-xl bg-ink-900 text-sand-50 shadow-soft">
                                    <Wand2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-heading font-semibold text-ink-900">Paramètres de génération</h2>
                                    <p className="text-xs text-ink-500">
                                        Ajuste les filtres puis lance la génération IA.
                                    </p>
                                </div>
                            </div>

                            <OutfitForm
                                formData={formData}
                                handleInputChange={handleInputChange}
                                generateOutfit={generateOutfit}
                                isLoading={isLoading}
                            />

                            {isGenerating && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-5 p-4 bg-sage-50 rounded-xl border border-sage-200"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="relative">
                                            <div className="w-6 h-6 border-2 border-sage-200 rounded-full" />
                                            <div className="absolute top-0 left-0 w-6 h-6 border-2 border-transparent border-t-sage-500 rounded-full animate-spin" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-sage-800">
                                                Génération en cours...
                                            </p>
                                            <p className="text-xs text-sage-600">
                                                L&apos;IA compose ton outfit idéal.
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </motion.section>

                        <motion.section
                            className="flex-1 flex flex-col space-y-0 lg:-mt-1"
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            {CATEGORIES.map((category, index) => (
                                <motion.div
                                    key={category.key}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 * index }}
                                >
                                    <MemoizedClothesSwiper
                                        items={itemsByCategory[category.key] || []}
                                        selectedId={selectedIds[category.key]}
                                        showDetails={false}
                                        heightClass="h-[280px] sm:h-[340px] lg:h-[420px]"
                                        containerClass="rounded-[32px] overflow-hidden bg-white/85 backdrop-blur border border-sand-100 shadow-[0_20px_48px_rgba(17,18,26,0.08)]"
                                    />
                                </motion.div>
                            ))}
                        </motion.section>
                    </div>

                    <motion.section
                        variants={itemVariants}
                        className="rounded-[32px] border border-sand-100 bg-white/85 backdrop-blur p-6 sm:p-8 shadow-[0_24px_60px_rgba(17,18,26,0.08)] space-y-6"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h2 className="text-xl font-heading font-semibold text-ink-900">
                                    Tenue actuelle
                                </h2>
                                <p className="text-sm text-ink-500">
                                    Un récapitulatif clair de chaque pièce sélectionnée par l&apos;IA.
                                </p>
                            </div>
                            <div className="inline-flex items-center gap-2 text-xs font-medium text-ink-500 bg-sand-100/80 border border-sand-200 rounded-full px-3 py-1.5">
                                <Sparkles className="w-4 h-4" />
                                {hasSelection ? "Tenue prête" : "En attente de génération"}
                            </div>
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                            {CATEGORIES.map((category) => {
                                const item = selectedOutfit[category.key]?.[0] || null;
                                const categoryMeta = {
                                    tops: {
                                        label: "Haut",
                                        icon: <Shirt className="w-5 h-5 text-ink-500" />,
                                    },
                                    bottoms: {
                                        label: "Bas",
                                        icon: <Layers className="w-5 h-5 text-ink-500" />,
                                    },
                                    shoes: {
                                        label: "Chaussures",
                                        icon: <Sparkles className="w-5 h-5 text-ink-500" />,
                                    },
                                }[category.key];

                                return (
                                    <div
                                        key={category.key}
                                        className="flex gap-4 rounded-3xl border border-sand-100 bg-white/90 p-4 shadow-soft"
                                    >
                                        <div className="w-16 h-16 rounded-2xl overflow-hidden border border-sand-200 bg-sand-50 flex items-center justify-center">
                                            {item?.image ? (
                                                <img
                                                    src={item.image}
                                                    alt={item.title || categoryMeta.label}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                categoryMeta.icon
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs uppercase tracking-[0.14em] text-ink-400">
                                                {categoryMeta.label}
                                            </p>
                                            <p className="mt-1 text-sm font-semibold text-ink-900">
                                                {item?.title?.trim() || item?.type || "Non sélectionné"}
                                            </p>
                                            <p className="text-xs text-ink-500 mt-1">
                                                {item
                                                    ? [
                                                          item.type ? item.type : null,
                                                          Array.isArray(item.colors)
                                                              ? item.colors.join(", ")
                                                              : item.color || null,
                                                      ]
                                                          .filter(Boolean)
                                                          .join(" • ") || "Détails indisponibles"
                                                    : "Ajoute tes pièces ou laisse l’IA en proposer."}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.section>

                    <motion.section
                        variants={itemVariants}
                        className="rounded-[32px] border border-sand-100 bg-white/80 p-6 sm:p-7 shadow-[0_20px_48px_rgba(17,18,26,0.08)]"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                            <div className="flex items-center gap-3">
                                <History className="w-5 h-5 text-ink-500" />
                                <h2 className="text-lg font-heading font-semibold text-ink-900">
                                    Historique des dernières tenues
                                </h2>
                            </div>
                            <p className="text-xs text-ink-500">
                                Jusqu&apos;à 5 propositions récentes pour revenir rapidement en arrière.
                            </p>
                        </div>
                        {resolvedRecentOutfits.length > 0 ? (
                            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                {resolvedRecentOutfits.map((outfit, index) => (
                                    <button
                                        key={`${outfit.ids.top || "x"}-${outfit.ids.bottom || "y"}-${outfit.ids.shoes || "z"}-${index}`}
                                        onClick={() => handleRestoreOutfit(outfit)}
                                        className="group text-left rounded-2xl border border-sand-100 bg-white/80 p-4 transition-all hover:border-ink-200 hover:shadow-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-ink-400 uppercase tracking-[0.14em]">
                                                Tenue {index + 1}
                                            </span>
                                            <RotateCcw className="w-4 h-4 text-ink-300 group-hover:text-ink-600" />
                                        </div>
                                        <p className="text-sm text-ink-800 font-semibold">
                                            {[
                                                outfit.top?.title || outfit.top?.type || "Haut",
                                                outfit.bottom?.title || outfit.bottom?.type || "Bas",
                                                outfit.shoes?.title || outfit.shoes?.type || "Chaussures",
                                            ].join(" • ")}
                                        </p>
                                        <p className="mt-2 text-xs text-ink-500">
                                            {index === 0 ? "Dernière génération" : "Proposition précédente"}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-sand-200 bg-sand-50/80 p-6 text-center">
                                <p className="text-sm font-medium text-ink-700">
                                    Aucune tenue enregistrée pour l’instant.
                                </p>
                                <p className="text-xs text-ink-500 mt-2">
                                    Génère une tenue pour commencer à remplir l’historique.
                                </p>
                            </div>
                        )}
                    </motion.section>
                </motion.div>
            </main>

            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </div>
    );
};

export default OutfitsToday;
